if (!customElements.get('product-info')) {
  function escapeHtmlForEaseSaleBadge(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          const isSalespageV1 =
            document.body.classList.contains('product-template') &&
            document.body.classList.contains('salespage-v1');
          const isSimplePdp =
            document.body.classList.contains('product-template') &&
            document.body.classList.contains('simple-pdp');
          if (isSalespageV1) {
            this.updateSalespagePrice(this.currentVariant);
            this.updateSalespageBundlePricing(this.currentVariant);
          }
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            const easeSticky =
              document.getElementById('shopify-section-ease_sticky_atc') ||
              document.getElementById('shopify-section-ease-sticky-atc-bar');
            const defaultSticky = document.getElementById('shopify-section-sticky-atc-bar');
            const stickyEl = easeSticky || defaultSticky;
            const stickyNext = easeSticky
              ? html.getElementById('shopify-section-ease_sticky_atc') ||
                html.getElementById('shopify-section-ease-sticky-atc-bar')
              : html.getElementById('shopify-section-sticky-atc-bar');
            if (stickyEl && stickyNext) {
              HTMLUpdateUtility.viewTransition(
                stickyEl,
                stickyNext,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const isSalespageV1 =
            document.body.classList.contains('product-template') &&
            document.body.classList.contains('salespage-v1');
          const isSimplePdp =
            document.body.classList.contains('product-template') &&
            document.body.classList.contains('simple-pdp');

          const productInfoInResponse = this.getProductInfoNodeFromHtml(html);
          let variant = this.getSelectedVariant(productInfoInResponse);

          if (!isSalespageV1 && !isSimplePdp) {
            this.updateOptionValues(html);
          }

          if (isSalespageV1 || isSimplePdp) {
            this.syncSelectedVariantJsonFromFetchedSection(html);
            if (!variant) {
              variant = this.getSelectedVariant(this);
            }
            if (!variant && isSimplePdp) {
              variant = this.resolveEaseSimpleVariantFromDom();
              if (variant) {
                const jsonEl = this.querySelector('variant-selects script[data-selected-variant]');
                if (jsonEl) jsonEl.textContent = JSON.stringify(variant);
              }
            }
          }

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          if (variant?.id) {
            this.updateVariantInputs(variant.id);
          }

          if (!variant) {
            if (isSimplePdp) {
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
                data: {
                  sectionId: this.sectionId,
                  html,
                  variant: null,
                },
              });
              document.dispatchEvent(
                new CustomEvent('variant:changed', {
                  detail: { variant: null },
                })
              );
              return;
            }
            this.setUnavailable();
            return;
          }

          const drawerOpen = this.isEaseStickyDrawerOpen();
          if (!(isSimplePdp && drawerOpen)) {
            this.updateMedia(variant);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          if (isSalespageV1) {
            this.updateSalespagePrice(variant);
            this.updateSalespageBundlePricing(variant);
          } else {
            updateSourceFromDestination('price');
          }
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          if (!isSimplePdp) {
            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
            this.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              FoxTheme.variantStrings.soldOut
            );
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      getEaseProductSavingsLabel() {
        const bundleWrapper = this.querySelector('.bundle-pricing-wrapper.ease-sp-bundle--salespage');
        const fromWrapper = (bundleWrapper?.dataset.easeProductSavingsLabel || '').trim();
        if (fromWrapper) return fromWrapper;

        const bundleEl = this.querySelector('#bundle-price-summary-' + this.dataset.section);
        const fromSummary = (bundleEl?.dataset.easeProductSavingsLabel || '').trim();
        if (fromSummary) return fromSummary;

        const fromRoot = (this.dataset.easeProductSavingsLabel || '').trim();
        if (fromRoot) return fromRoot;

        const htmlLang = (document.documentElement.getAttribute('lang') || 'de').split('-')[0].toLowerCase();
        return htmlLang === 'en' ? 'Product savings' : 'Produktersparnis';
      }

      updateSalespagePrice(variant) {
        if (!variant) return;

        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceContainer) return;

        const priceRoot = priceContainer.querySelector('.ease-salespage-info-price');
        const innerContainer = priceContainer.querySelector('.price__container');
        if (!priceRoot || !innerContainer) return;

        const moneyFormat = FoxTheme.settings.moneyFormat;
        const currencyEnabled = !!FoxTheme.settings.currencyCodeEnabled;
        const formatMoney = (amount) =>
          currencyEnabled
            ? FoxTheme.Currency.formatMoney(amount, FoxTheme.settings.moneyWithCurrencyFormat || moneyFormat)
            : FoxTheme.Currency.formatMoney(amount, moneyFormat);
        const formatWholeMoney = (amount) => formatMoney(amount).replace(/([,.]\d{2})(?=\D|$)/, '');

        const price = variant.price;
        const bundleEl = this.querySelector('#bundle-price-summary-' + this.dataset.section);
        const extrasEuros = bundleEl ? parseFloat(bundleEl.dataset.totalSavings || '0', 10) || 0 : 0;
        const extrasCents = Math.round(extrasEuros * 100);
        const compareAtRaw = variant.compare_at_price || variant.price;
        const compareAtDisplay = compareAtRaw;
        const onSale = !!(compareAtRaw && compareAtRaw > price);
        const available = !!variant.available;

        priceRoot.classList.toggle('price--on-sale', onSale);
        priceRoot.classList.toggle('price--sold-out', !available);

        const badgeEnabled = priceContainer.dataset.easeSaleBadgeEnabled !== 'false';
        const customBadgeLine = (priceContainer.dataset.easeSaleBadgeLine || '').trim();
        const fallbackBadgeLine = '-10%';
        const badgeLine = badgeEnabled ? customBadgeLine || fallbackBadgeLine : '';
        innerContainer.setAttribute('data-ease-sale-badge-line', badgeLine);
        const badgeSavingHtml = badgeLine
          ? `<span class="ease-salespage-info-price__saving">
                <span class="ease-salespage-info-price__saving-main">${escapeHtmlForEaseSaleBadge(badgeLine)}</span>
              </span>`
          : '';
        const savingsPrefix = this.getEaseProductSavingsLabel();
        const extrasLabel =
          extrasCents > 0 ? `${savingsPrefix}: ${formatWholeMoney(extrasCents)}` : '';
        const badgeTextColor = priceContainer.dataset.easeSaleBadgeText || '#c73a3a';
        const badgeBgColor = priceContainer.dataset.easeSaleBadgeBg || badgeTextColor;
        priceContainer.style.setProperty('--ease-sp-badge-text', badgeTextColor);
        priceContainer.style.setProperty('--ease-sp-badge-bg', badgeBgColor);

        this.updateSalespageBundleHeaderSavings(extrasLabel);

        if (onSale) {
          innerContainer.innerHTML = `
            <div class="price__sale">
              <span class="price-item price-item--sale price-item--last">${formatWholeMoney(price)}</span>
              <span><s class="price-item price-item--regular">${formatWholeMoney(compareAtDisplay)}</s></span>
              ${badgeSavingHtml}
            </div>
          `;
        } else {
          innerContainer.innerHTML = `
            <div class="price__regular">
              <span class="price-item price-item--regular">${formatWholeMoney(price)}</span>
              ${badgeSavingHtml}
            </div>
          `;
        }
      }

      updateSalespageBundleHeaderSavings(extrasLabel) {
        const bundleTitle = this.querySelector('.bundle-pricing-wrapper.ease-sp-bundle--salespage .gratis-extras-title');
        if (!bundleTitle) return;

        let savingsInline = bundleTitle.querySelector('.ease-salespage-inline-saving-sub');
        if (!extrasLabel) {
          if (savingsInline) savingsInline.remove();
          return;
        }

        if (!savingsInline) {
          savingsInline = document.createElement('span');
          savingsInline.className = 'ease-salespage-inline-saving-sub';
          bundleTitle.append(' ');
          bundleTitle.appendChild(savingsInline);
        }
        savingsInline.textContent = extrasLabel;
      }

      /**
       * Buy-buttons bundle block (Mit dabei / Gesamt): auf salespage-v1 wird variant-selects im DOM
       * nicht per fetch aktualisiert - Bundle-Preis daher hier mit derselben Varianten-Quelle wie der Kopfpreis.
       */
      updateSalespageBundlePricing(variant) {
        if (!variant || variant.price == null) return;

        const bundleEl = this.querySelector('#bundle-price-summary-' + this.dataset.section);
        if (!bundleEl) return;

        const moneyFormat = FoxTheme.settings.moneyFormat;
        const currencyEnabled = !!FoxTheme.settings.currencyCodeEnabled;
        const formatMoney = (amount) =>
          currencyEnabled
            ? FoxTheme.Currency.formatMoney(amount, FoxTheme.settings.moneyWithCurrencyFormat || moneyFormat)
            : FoxTheme.Currency.formatMoney(amount, moneyFormat);
        const formatWholeMoney = (amount) => formatMoney(amount).replace(/([,.]\d{2})(?=\D|$)/, '');

        const extrasEuros = parseFloat(bundleEl.dataset.totalSavings || '0', 10) || 0;
        const extrasCents = Math.round(extrasEuros * 100);
        const originalBaseCents = variant.compare_at_price || variant.price;
        const fullOldCents = originalBaseCents + extrasCents;
        const finalCents = variant.price;
        const savingsCents = Math.max(0, fullOldCents - finalCents);

        const originalTotalEl = bundleEl.querySelector('.original-total');
        const finalTotalEl = bundleEl.querySelector('.final-total');
        const ersparnisMoneyEl = bundleEl.querySelector('.ease-sp-bundle__ersparnis-money');
        const ersparnisPctEl = bundleEl.querySelector('.ease-sp-bundle__ersparnis-pct');
        const ersparnisLegacyEl =
          !ersparnisMoneyEl && bundleEl.querySelector('.ease-sp-bundle__ersparnis');

        if (originalTotalEl) originalTotalEl.textContent = formatWholeMoney(fullOldCents);
        if (finalTotalEl) finalTotalEl.textContent = formatWholeMoney(finalCents);

        const pct =
          fullOldCents > 0 && savingsCents > 0
            ? Math.round((savingsCents / fullOldCents) * 100)
            : 0;
        const htmlLang = (document.documentElement.getAttribute('lang') || 'de').split('-')[0].toLowerCase();
        const pctLabel = htmlLang === 'en' ? '(' + pct + '%)' : '(−' + pct + ' %)';

        if (ersparnisMoneyEl) {
          ersparnisMoneyEl.textContent = '-' + formatWholeMoney(savingsCents);
          if (ersparnisPctEl) {
            if (pct > 0) {
              ersparnisPctEl.textContent = pctLabel;
              ersparnisPctEl.removeAttribute('hidden');
            } else {
              ersparnisPctEl.textContent = '';
              ersparnisPctEl.setAttribute('hidden', '');
            }
          }
        } else if (ersparnisLegacyEl) {
          ersparnisLegacyEl.textContent = '-' + formatWholeMoney(savingsCents);
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        const cleanOptionValues = (optionValues || []).filter((id) => id != null && id !== '');
        if (cleanOptionValues.length) {
          params.push(`option_values=${cleanOptionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getProductInfoNodeFromHtml(html) {
        if (!html) return null;
        const sectionId = this.sectionId;
        return (
          html.querySelector(`product-info[data-section="${sectionId}"]`) ||
          html.querySelector('product-info.ease-simple-pdp') ||
          html.querySelector('product-info')
        );
      }

      isEaseStickyDrawerOpen() {
        if (!document.body.classList.contains('ease-sticky-drawer-open')) return false;
        const drawer = document.querySelector('.ease-sticky-atc-drawer, modal-component.ease-sticky-atc-drawer');
        if (!drawer) return false;
        return (
          drawer.hasAttribute('open') ||
          drawer.hasAttribute('active') ||
          drawer.open === true
        );
      }

      resolveEaseSimpleVariantFromDom() {
        const configEl = this.querySelector('[data-ease-simple-pdp-config]');
        if (!configEl) return null;

        let config;
        try {
          config = JSON.parse(configEl.textContent);
        } catch (e) {
          return null;
        }

        const variants = config?.variants;
        if (!Array.isArray(variants) || !variants.length) return null;

        const variantSelects = this.variantSelectors;
        if (!variantSelects) return null;

        const selectedByIndex = {};
        variantSelects.querySelectorAll('fieldset.product-form__input').forEach((fieldset, index) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked, select option[selected]');
          if (!checked) return;
          selectedByIndex[index] = checked.value;
        });

        return (
          variants.find((candidate) => {
            if (!candidate?.options?.length) return false;
            return candidate.options.every((optionValue, index) => {
              const selected = selectedByIndex[index];
              return selected == null || selected === optionValue;
            });
          }) || null
        );
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode?.querySelector) return null;
        const el = productInfoNode.querySelector('variant-selects script[data-selected-variant]');
        const raw =
          el?.textContent?.trim() ||
          productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (!raw) return null;
        try {
          const parsed = JSON.parse(raw);
          return parsed && parsed.id ? parsed : null;
        } catch (e) {
          return null;
        }
      }

      /**
       * Salespage-v1: updateOptionValues() wird absichtlich nicht ausgeführt (kein kompletter Austausch von variant-selects).
       * Ohne Sync bleibt <script data-selected-variant> auf der ersten Variante; media-gallery init() liest nach 100ms
       * dieses veraltete JSON und sortiert die Galerie zurück (z. B. auf Gold).
       */
      syncSelectedVariantJsonFromFetchedSection(html) {
        const sectionId = this.dataset.section;
        const sourceRoot = this.getProductInfoNodeFromHtml(html) || html;
        const sourceVs =
          sourceRoot?.querySelector(`variant-selects[data-section="${sectionId}"]`) ||
          sourceRoot?.querySelector('variant-selects');
        const destVs =
          this.querySelector(`variant-selects[data-section="${sectionId}"]`) || this.querySelector('variant-selects');
        if (!sourceVs || !destVs) return;
        const src = sourceVs.querySelector('script[data-selected-variant]');
        const dest = destVs.querySelector('script[data-selected-variant]');
        if (!src || !dest) return;
        const text = (src.textContent && src.textContent.trim()) || src.innerHTML;
        if (!text) return;
        dest.textContent = text;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            const focusEl = document.querySelector(`#${targetId}`);
            if (!focusEl) return;
            const drawerOpen = this.isEaseStickyDrawerOpen();
            focusEl.focus({ preventScroll: drawerOpen });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText =
          productForm.querySelector('[name="add"] .product-form__submit-label') ||
          productForm.querySelector('[name="add"] > span');
        if (!addButton || !addButtonText) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');
        const addButtonText =
          button.querySelector('[name="add"] .product-form__submit-label') || button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton || !addButtonText) return;
        addButtonText.textContent = FoxTheme.variantStrings.unavailable;
        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
